import React, { memo } from 'react';
import styled from 'styled-components';

import COLORS from './colors';
import TYPO from './typography';

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const RangeWrapper = styled.div`
  width: 96px;
  height: 12px;
  position: relative;
  cursor: pointer;
`;

const InlineValue = styled.div`
  margin-left: 12px;
  ${TYPO.small}
`;

type ProgressRangeProps = {
  $width: number;
};
const ProgressRange = styled.div<ProgressRangeProps>`
  width: ${(props) => props.$width}%;
  height: 100%;
  background: ${COLORS.brand.regular.css};
  z-index: 2;
`;

type MarkerProps = {
  $position: number;
  $value: number;
};

const Marker = styled.div<MarkerProps>`
  width: 1px;
  height: 8px;
  position: absolute;
  top: 2px;
  left: ${(props) => `calc(${props.$position}% - 1px)`};
  background: ${(props) =>
    props.$value > props.$position ? COLORS.white.css : COLORS.shades.s200.css};
  z-index: 2;
`;

const TrackRange = styled.div`
  width: 100%;
  height: 6px;
  position: absolute;
  top: 3px;
  left: 0;
  z-index: 1;
  background: ${COLORS.shades.s200.css};
  box-shadow: inset 0 0 0 1px ${COLORS.shades.s300.css};
  border-radius: 10px;
  overflow: hidden;
`;

const Range = styled.input`
  -webkit-appearance: none;
  width:100%;
  position:absolute;
  z-index:3;
  top:3px;
  left:0;
  margin:0;
  background: transparent;
  &:focus {
    outline: none;
  }
  &:hover {
    &::-webkit-slider-thumb {
      background: ${COLORS.shades.s100.css};
    }
  }
  &:active {
    outline: none;
    &::-webkit-slider-thumb {
      border: 1px solid ${COLORS.brand.regular.css};
    }
  }
  &::-webkit-slider-runnable-track {
    height: 6px;
    cursor: pointer;
    background: rgba(0,0,0,0);
  }
  &::-webkit-slider-thumb {
    border: 1px solid ${COLORS.shades.s300.css};
    height: 12px;
    width: 12px;
    border-radius: 50%;
    background: ${COLORS.shades.s200.css};
    -webkit-appearance: none;
    margin-top: -3px;
    opacity:1;
  }
  }
`;

type InputSliderProps = {
  min?: number;
  max?: number;
  value?: number;
  onChange: (val: number) => void;
  defaultValue?: number;
  step?: number;
  displayValue?: string;
};

function InputSlider({
  min = 0,
  max = 100,
  value = 10,
  onChange,
  defaultValue,
  step = 1,
  displayValue = '',
  ...props
}: InputSliderProps) {
  const getPercentage = (val: number) => ((val - min) * 100) / (max - min);

  return (
    <Container {...props}>
      <RangeWrapper>
        <Range
          type="range"
          min={min}
          max={max}
          list="steplist"
          value={value}
          onChange={(e) => onChange(+e.target.value)}
          onDoubleClick={defaultValue ? () => onChange(defaultValue) : null}
          step={step}
        />

        {defaultValue && (
          <datalist id="steplist">
            <option>{defaultValue}</option>
          </datalist>
        )}

        {defaultValue && (
          <Marker
            $position={getPercentage(defaultValue)}
            $value={getPercentage(value)}
          />
        )}

        <TrackRange>
          <ProgressRange $width={getPercentage(value)} />
        </TrackRange>
      </RangeWrapper>
      {displayValue && (
        <InlineValue>
          {displayValue === 'percentage'
            ? `${parseInt(String(getPercentage(value)), 10)}%`
            : value}
        </InlineValue>
      )}
    </Container>
  );
}

export default memo(InputSlider);
